import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import ResourceCard from '../components/ResourceCard';
import ResourceComposerModal from '../components/ResourceComposerModal';

const LIMIT = 24;

const LANGUAGES = [
  { key: 'zh-Hans', label: '中文' },
  { key: 'en', label: 'English' },
  { key: 'ja', label: '日本語' },
  { key: 'other', label: '其他' },
];

interface Filters {
  category: string;
  type: string;
  software: string;
  format: string;
  provider: string;
  license: string;
  language: string;
  source: string;
  q: string;
}

export default class ResourcesPage extends Page {
  filters: Filters = {
    category: '',
    type: '',
    software: '',
    format: '',
    provider: '',
    license: '',
    language: '',
    source: '',
    q: '',
  };

  cards: any[] = [];
  loading = true;
  offset = 0;
  hasMore = false;
  filtersOpen = false;

  oninit(vnode: any) {
    super.oninit(vnode);
    this.readFiltersFromRoute();
    this.refresh();
  }

  readFiltersFromRoute() {
    const p = m.route.param() as Record<string, string>;
    this.filters = {
      category: p.category || '',
      type: p.type || '',
      software: p.software || '',
      format: p.format || '',
      provider: p.provider || '',
      license: p.license || '',
      language: p.language || '',
      source: p.source || '',
      q: p.q || '',
    };
  }

  applyFilter(key: string, value: string) {
    this.filters[key] = value;
    this.offset = 0;
    this.syncRoute();
    this.refresh();
  }

  syncRoute() {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(this.filters)) {
      if (v) params[k] = v;
    }
    m.route.set(app.route('resources'), params);
  }

  async refresh() {
    this.loading = true;
    m.redraw();

    const params: Record<string, string> = {
      'page[limit]': String(LIMIT),
      'page[offset]': String(this.offset),
      include: 'discussion,images,links',
    };

    const map: Record<string, string> = {
      category: 'filter[category]',
      type: 'filter[resourceType]',
      software: 'filter[software]',
      format: 'filter[format]',
      provider: 'filter[provider]',
      license: 'filter[license]',
      language: 'filter[language]',
      source: 'filter[sourceType]',
      q: 'filter[q]',
    };

    for (const [key, filter] of Object.entries(map)) {
      if (this.filters[key as keyof Filters]) {
        params[filter] = this.filters[key as keyof Filters];
      }
    }

    const query = Object.entries(params)
      .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v))
      .join('&');

    try {
      const result = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/resources?' + query,
      });

      const included = result.included || [];
      const resolve = (type: string, id: any) => included.find((x: any) => x.type === type && String(x.id) === String(id));

      this.cards = (result.data || []).map((r: any) => {
        const attrs = r.attributes;
        const discussion = resolve('discussions', r.relationships?.discussion?.data?.id);
        const imageIds = (r.relationships?.images?.data || []).map((i: any) => i.id);
        const images = imageIds.map((id: any) => resolve('resource-images', id)).filter(Boolean);
        const cover = images.find((i: any) => i.attributes?.type === 'cover') || images[0];
        const linkIds = (r.relationships?.links?.data || []).map((l: any) => l.id);
        const providers = linkIds.map((id: any) => resolve('resource-links', id)?.attributes?.provider).filter(Boolean);

        return {
          resourceId: r.id,
          discussionId: r.relationships?.discussion?.data?.id,
          title: discussion?.attributes?.title || '(无标题)',
          cover,
          providers,
          attrs,
        };
      });

      this.hasMore = !!result.links?.next;
    } catch (e) {
      this.cards = [];
      this.hasMore = false;
    }

    this.loading = false;
    m.redraw();
  }

  view() {
    const rh = app.forum.attribute('resourceHub') || {};
    const tabs = [{ key: '', label: '全部' }, ...(rh.categories || [])];
    const typeOptions = rh.categories?.find((c: any) => c.key === this.filters.category)?.types || [];

    return (
      <div className="ResourcesPage">
        <div className="container">
          <div className="ResourcesHeader">
            <div className="ResourcesHeader-title">
              <h2>资源中心</h2>
              <span className="ResourcesCount">{this.loading ? '加载中…' : `${this.cards.length}${this.hasMore ? '+' : ''} 个资源`}</span>
            </div>
            <div className="ResourcesHeader-actions">
              <input
                className="FormControl ResourcesSearch"
                placeholder="搜索资源…"
                value={this.filters.q}
                oninput={(e: any) => (this.filters.q = e.target.value)}
                onkeydown={(e: any) => {
                  if (e.key === 'Enter') this.applyFilter('q', this.filters.q);
                }}
              />
              {app.forum?.attribute('resourceHub')?.canCreate && (
                <Button className="Button Button--primary" icon="fas fa-plus" onclick={() => app.modal.show(ResourceComposerModal)}>
                  发布资源
                </Button>
              )}
            </div>
          </div>

          <div className="ResourcesTabs">
            {tabs.map((c: any) => (
              <button
                className={'ResourcesTab' + (this.filters.category === c.key ? ' active' : '')}
                onclick={() => this.applyFilter('category', this.filters.category === c.key ? '' : c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <button className="ResourcesFiltersToggle Button" onclick={() => { this.filtersOpen = !this.filtersOpen; m.redraw(); }}>
            <i className="fas fa-sliders-h" /> 筛选
          </button>

          <div className="ResourcesLayout">
            <aside className={'ResourcesFilters' + (this.filtersOpen ? ' open' : '')}>
              <div className="ResourcesFilters-head">
                <span className="ResourcesFilters-title">
                  <i className="fas fa-sliders-h" /> 筛选
                </span>
                {(this.filters.type || this.filters.software || this.filters.format || this.filters.provider || this.filters.language || this.filters.source || this.filters.license) && (
                  <button className="ResourcesFilters-clear" onclick={() => this.clearFilters()}>
                    清除全部
                  </button>
                )}
              </div>

              <div className="FilterGroup">
                <label>资源类型</label>
                <select
                  className="FormControl"
                  value={this.filters.type}
                  onchange={(e: any) => this.applyFilter('type', e.target.value)}
                >
                  <option value="">全部</option>
                  {typeOptions.map((t: any) => (
                    <option value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>适用软件</label>
                <select className="FormControl" value={this.filters.software} onchange={(e: any) => this.applyFilter('software', e.target.value)}>
                  <option value="">全部</option>
                  {(rh.software || []).map((s: any) => (
                    <option value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>资源格式</label>
                <select className="FormControl" value={this.filters.format} onchange={(e: any) => this.applyFilter('format', e.target.value)}>
                  <option value="">全部</option>
                  {(rh.formats || []).map((f: any) => (
                    <option value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>网盘 / 来源</label>
                <select className="FormControl" value={this.filters.provider} onchange={(e: any) => this.applyFilter('provider', e.target.value)}>
                  <option value="">全部</option>
                  {(rh.providers || []).map((p: any) => (
                    <option value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>语言</label>
                <select className="FormControl" value={this.filters.language} onchange={(e: any) => this.applyFilter('language', e.target.value)}>
                  <option value="">全部</option>
                  {LANGUAGES.map((l: any) => (
                    <option value={l.key}>{l.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>来源类型</label>
                <select className="FormControl" value={this.filters.source} onchange={(e: any) => this.applyFilter('source', e.target.value)}>
                  <option value="">全部</option>
                  {(rh.sourceTypes || []).map((s: any) => (
                    <option value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="FilterGroup">
                <label>授权 (License)</label>
                <input
                  className="FormControl"
                  value={this.filters.license}
                  placeholder="如 CC BY"
                  oninput={(e: any) => (this.filters.license = e.target.value)}
                  onkeydown={(e: any) => {
                    if (e.key === 'Enter') this.applyFilter('license', this.filters.license);
                  }}
                />
              </div>

              {(this.filters.type || this.filters.software || this.filters.format || this.filters.provider || this.filters.language || this.filters.source || this.filters.license) && (
                <Button className="Button Button--block" onclick={() => this.clearFilters()}>
                  清除筛选
                </Button>
              )}
            </aside>

            <div className="ResourcesContent">
              {this.loading ? (
                <div className="ResourcesLoading">加载中…</div>
              ) : this.cards.length === 0 ? (
                <div className="ResourcesEmpty">暂无资源，点击右上角「发布资源」上传第一个资源吧～</div>
              ) : (
                <div className="ResourcesGrid">
                  {this.cards.map((card: any) => (
                    <ResourceCard card={card} key={card.discussionId} />
                  ))}
                </div>
              )}

              {(this.offset > 0 || this.hasMore) && (
                <div className="ResourcesPagination">
                  <Button className="Button" disabled={this.offset === 0} onclick={() => { this.offset = Math.max(0, this.offset - LIMIT); this.refresh(); }}>
                    上一页
                  </Button>
                  <Button className="Button" disabled={!this.hasMore} onclick={() => { this.offset += LIMIT; this.refresh(); }}>
                    下一页
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  clearFilters() {
    this.filters = { category: '', type: '', software: '', format: '', provider: '', license: '', language: '', source: '', q: '' };
    this.offset = 0;
    this.syncRoute();
    this.refresh();
  }
}
